import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  createTest,
  updateTest,
  deleteTest,
  enableTest,
  lockTest,
  fetchTestsForSession,
  fetchTestProgress,
  createSessionTest,
  moveTest,
  reorderClassTests,
  reorderSessionTests,
  pushTestToBank,
} from "../../api/backend";
import CreateTestModal from "../../components/CreateTestModal";
import HowToGuideBox from "../../components/HowToGuideBox";
import AddFromBankModal from "../../components/AddFromBankModal";
import SaveToBankPromptDialog from "../../components/SaveToBankPromptDialog";
import Icon from "./testsList/Icon";
import { moveMenuItems } from "../../components/moveMenu";
import TestRow from "./testsList/TestRow";
import AnswerKeyDialog from "./testsList/AnswerKeyDialog";
import { buildTestRow, countByStatus, filterTests, sessionHeading } from "./testsList/testsModel";
import "./testsList/testsList.css";

// ─── One test row with its drag, reorder and overflow behaviour ───────────────
function TestCard({
  test, canEdit, contentLocked = false, progress, totalStudents, onEdit, onDelete, onToggleEnabled, onToggleLocked, onAssess, onAnswerKey,
  sessions, onMove, draggable, onDragStart, onDragEnd, dragging,
  showReorder, onMoveUp, onMoveDown, moveUpDisabled, moveDownDisabled,
  canDropHere, onDropBefore,
}) {
  const [dropOver, setDropOver] = React.useState(false);
  const model = React.useMemo(() => buildTestRow(test, progress, totalStudents), [test, progress, totalStudents]);
  // canEdit governs the publish/enable toggle and lock; editable governs every control that changes content.
  const editable = canEdit && !contentLocked;
  const moveLocked = !editable || Boolean(test.inLearningPlan);

  const moveIcon = (kind) => <Icon n={kind === "class" ? "layers" : "cal"} s={15} />;
  const moveSection = moveMenuItems({ item: test, sessions, onMove, locked: moveLocked, classLabel: "Class-level tests", icon: moveIcon });
  const menuItems = [
    { icon: <Icon n="chart" s={15} />, label: "View results", onSelect: onAssess },
    { icon: <Icon n="key" s={15} />, label: "View answer key", onSelect: onAnswerKey },
    ...(editable ? [{ icon: <Icon n="edit" s={15} />, label: "Edit test", onSelect: onEdit }] : []),
    ...(canEdit
      ? [{ icon: <Icon n={test.locked ? "unlock" : "lock"} s={15} />, label: test.locked ? "Unlock test" : "Lock test", onSelect: onToggleLocked }]
      : []),
    ...moveSection,
    ...(editable ? [{ separator: true }, { icon: <Icon n="trash" s={15} />, label: "Delete test", danger: true, onSelect: onDelete }] : []),
  ];

  return (
    <TestRow
      model={model}
      canEdit={canEdit}
      onTogglePublish={onToggleEnabled}
      onResults={onAssess}
      onAnswerKey={onAnswerKey}
      menuItems={menuItems}
      dragging={dragging}
      dropOver={dropOver}
      reorder={showReorder && editable ? { upDisabled: moveUpDisabled || moveLocked, downDisabled: moveDownDisabled || moveLocked, onUp: onMoveUp, onDown: onMoveDown } : null}
      dragProps={{
        draggable: Boolean(draggable) && editable && !moveLocked,
        onDragStart: () => onDragStart?.(test),
        onDragEnd: () => onDragEnd?.(),
        onDragOver: (e) => {
          if (canDropHere && !moveLocked) {
            e.preventDefault();
            e.stopPropagation();
            setDropOver(true);
          }
        },
        onDragLeave: () => setDropOver(false),
        onDrop: (e) => {
          if (!canDropHere || moveLocked) return;
          e.preventDefault();
          e.stopPropagation();
          setDropOver(false);
          onDropBefore?.();
        },
      }}
    />
  );
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────
function Toolbar({ filter, setFilter, counts, query, setQuery, onAddTest, onFromBank, canEdit }) {
  const chips = [
    { id: "all", label: "All" },
    { id: "live", label: "Live" },
    { id: "draft", label: "Drafts" },
    { id: "locked", label: "Locked" },
  ];
  return (
    <div className="bar-t">
      <label className="search">
        <Icon n="search" s={15} />
        <input type="search" placeholder="Search tests" aria-label="Search tests" value={query} onChange={(e) => setQuery(e.target.value)} />
      </label>
      <div className="seg" role="group" aria-label="Filter tests">
        {chips.map((c) => (
          <button key={c.id} type="button" className={filter === c.id ? "on" : ""} aria-pressed={filter === c.id} onClick={() => setFilter(c.id)}>
            {c.label} <b>{counts[c.id] ?? 0}</b>
          </button>
        ))}
      </div>
      <span className="sp" />
      <HowToGuideBox
          iconOnly
          title="How to Create and Manage Written Tests"
          role="Instructors & Course Coordinators"
          steps={[
            {
              number: 1,
              title: "Create a Test",
              description: "Click 'Add Test' to open the test builder.",
              details: "Give the test a title, set a time limit, and configure retake settings.",
            },
            {
              number: 2,
              title: "Add Questions",
              description: "Use the question builder to add multiple question types.",
              details: [
                "Multiple Choice — one correct answer",
                "True / False — binary yes/no",
                "Multiple Select — one or more correct answers",
                "Matching — drag answers to match prompts",
              ],
            },
            {
              number: 3,
              title: "Set Opens / Closes Dates",
              description: "Optionally restrict when students can take the test.",
              details: "Leave blank to allow access at any time while the test is enabled.",
            },
            {
              number: 4,
              title: "Enable the Test",
              description: "Toggle the test on when students are ready to take it.",
              details: "Disabled tests are hidden from students.",
            },
            {
              number: 5,
              title: "Review Results",
              description: "Click a test to see individual student scores and answer breakdowns.",
              details: ["Score is calculated automatically on submission", "Retakes are allowed up to the configured limit"],
            },
          ]}
          tips={[
            "Preview the test yourself before enabling it for students.",
            "Lock the test after all students have completed it to prevent further changes.",
            "Use Multiple Select and Matching for higher-order skill assessments.",
            "Set a reasonable time limit — students can see the countdown while testing.",
          ]}
        />
      {canEdit && onFromBank && (
        <button type="button" className="btn btn-o" onClick={onFromBank}><Icon n="plus" />From Bank</button>
      )}
      {canEdit && (
        <button type="button" className="btn btn-p" onClick={onAddTest}><Icon n="plus" />New Test</button>
      )}
    </div>
  );
}

// ─── Session group ────────────────────────────────────────────────────────────
function SessionRow({
  session, sessionTests, canEdit, contentLocked = false, progressByTestId = {}, totalStudents, onAddTest, onToggleEnabled, onToggleLocked, onDelete, onEdit, onAssess, onAnswerKey,
  sessions, onMove, draggedItem, onDragStartItem, onDragEndItem, onDropTarget, onDropOnItem,
}) {
  const [dragOver, setDragOver] = React.useState(false);
  const locked = Boolean(session.locked);
  const editable = canEdit && !contentLocked;
  const canDrop = editable && !locked && draggedItem;
  const heading = sessionHeading(session);
  const tests = filterTests(sessionTests, "all", "");

  return (
    <div
      className={`sblk${dragOver ? " over" : ""}`}
      data-testid={`session-tests-${session.id}`}
      onDragOver={(e) => { if (canDrop) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (canDrop) onDropTarget?.(session.id);
      }}
    >
      <div className="grp">
        <b>{heading.label}</b>
        {heading.date && <span>{heading.date}</span>}
        {heading.detail && <span>{heading.detail}</span>}
        {locked && <span className="lock"><Icon n="lock" s={12} />Locked</span>}
        {editable && !locked && (
          <button type="button" className="btn btn-o btn-sm" onClick={onAddTest}><Icon n="plus" />Add Test</button>
        )}
      </div>
      {sessionTests === undefined ? (
        <div className="none">Loading tests…</div>
      ) : tests.length === 0 ? (
        <div className="none">No tests attached</div>
      ) : (
        tests.map((t, idx) => (
          <TestCard
            key={t.id}
            test={t}
            canEdit={canEdit && !locked}
            contentLocked={contentLocked}
            progress={progressByTestId[t.id]}
            totalStudents={totalStudents}
            onEdit={() => onEdit(t, session.id)}
            onDelete={() => onDelete(t)}
            onToggleEnabled={() => onToggleEnabled(t)}
            onToggleLocked={() => onToggleLocked(t)}
            onAssess={() => onAssess(t)}
            onAnswerKey={() => onAnswerKey(t)}
            sessions={sessions}
            onMove={editable && !locked ? onMove : null}
            draggable={editable && !locked}
            onDragStart={onDragStartItem}
            onDragEnd={onDragEndItem}
            dragging={draggedItem?.id === t.id}
            canDropHere={editable && !locked && draggedItem && draggedItem.id !== t.id}
            onDropBefore={() => onDropOnItem?.(t.id)}
            showReorder={editable && !locked && tests.length > 1}
            moveUpDisabled={idx === 0}
            moveDownDisabled={idx === tests.length - 1}
            onMoveUp={() => onDropOnItem?.(tests[idx - 1]?.id ?? null, t)}
            onMoveDown={() => onDropOnItem?.(tests[idx + 2]?.id ?? null, t)}
          />
        ))
      )}
      {dragOver && <div className="drop">Drop here to move this test to {heading.label}</div>}
    </div>
  );
}

// ─── WrittenTestsTab ──────────────────────────────────────────────────────────
export default function WrittenTestsTab({ course, sessions, tests, setTests, canEdit, contentLocked = false }) {
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  // Content edits (create, edit, delete, move, reorder) need canEdit and an unlocked class. The publish toggle and
  // lock still use canEdit alone.
  const editContent = canEdit && !contentLocked;

  const [sessionTests, setSessionTests] = React.useState({});
  const [loadedSessions, setLoadedSessions] = React.useState(new Set());

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editTarget, setEditTarget] = React.useState(null);
  const [editSessionId, setEditSessionId] = React.useState(null);

  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const [error, setError] = React.useState("");
  const [toast, setToast] = React.useState("");

  const [filter, setFilter] = React.useState("all");
  const [query, setQuery] = React.useState("");

  const [bankModalOpen, setBankModalOpen] = React.useState(false);

  // Submission counts keyed by testId
  const [progressByTestId, setProgressByTestId] = React.useState({});

  const [draggedTest, setDraggedTest] = React.useState(null);
  const [classDropOver, setClassDropOver] = React.useState(false);

  const [bankPromptTarget, setBankPromptTarget] = React.useState(null);
  const [keyTest, setKeyTest] = React.useState(null);
  const [classSize, setClassSize] = React.useState(null);

  React.useEffect(() => {
    fetchTestProgress(courseId)
      .then((data) => {
        const map = {};
        (data?.byTest || []).forEach((t) => { map[t.testId] = t; });
        setProgressByTestId(map);
        setClassSize(typeof data?.totalStudents === "number" ? data.totalStudents : null);
      })
      .catch(() => {});
  }, [courseId]);

  // Load session tests
  React.useEffect(() => {
    if (!sessions || sessions.length === 0) return;
    sessions.forEach((s) => {
      if (loadedSessions.has(s.id)) return;
      fetchTestsForSession(courseId, s.id).then((data) => {
        setSessionTests((prev) => ({ ...prev, [s.id]: data || [] }));
        setLoadedSessions((prev) => new Set([...prev, s.id]));
      });
    });
  }, [sessions, courseId, loadedSessions]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleOpenCreate = (sessionId = null) => {
    if (!editContent) return;
    setEditTarget(null);
    setEditSessionId(sessionId);
    setDialogOpen(true);
  };

  const handleOpenEdit = (test, sessionId = null) => {
    if (!editContent) return;
    setEditTarget(test);
    setEditSessionId(sessionId);
    setDialogOpen(true);
  };

  // Auto-save sends the id of a test it already saved, so it updates that test rather than creating a copy. It stays
  // quiet: no toast and no bank prompt, which belong to an explicit Save.
  const handleSave = async (formData) => {
    const { autosave, savedBankId, ...body } = formData;
    const targetId = editTarget?.id ?? savedBankId ?? null;
    if (targetId) {
      const updated = await updateTest(targetId, body);
      if (updated.sessionId != null) {
        setSessionTests((prev) => ({
          ...prev,
          [updated.sessionId]: (prev[updated.sessionId] || []).map((t) =>
            t.id === updated.id ? updated : t,
          ),
        }));
      } else {
        setTests((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      }
      if (!autosave) {
        setToast(updated.enabled ? "Test saved (still live)" : "Test saved");
        if (updated.sourceTestBankId) setBankPromptTarget(updated);
      }
      return { id: updated.id };
    }
    let created;
    if (editSessionId != null) {
      created = await createSessionTest(courseId, editSessionId, body);
      setSessionTests((prev) => ({
        ...prev,
        [editSessionId]: [...(prev[editSessionId] || []), created],
      }));
    } else {
      created = await createTest(courseId, body);
      setTests((prev) => [...prev, created]);
    }
    if (!autosave) setToast(created.enabled ? "Test published" : "Test saved as draft");
    return { id: created.id };
  };

  const handleDelete = async () => {
    if (!editContent || !deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      await deleteTest(deleteTarget.id);
      if (deleteTarget.sessionId != null) {
        setSessionTests((prev) => ({
          ...prev,
          [deleteTarget.sessionId]: (prev[deleteTarget.sessionId] || []).filter(
            (t) => t.id !== deleteTarget.id,
          ),
        }));
      } else {
        setTests((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      }
    } catch {
      setError("Delete failed.");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleToggleEnabled = async (test) => {
    try {
      const updated = await enableTest(test.id, !test.enabled);
      updateInState(updated, test.sessionId);
      setToast(updated.enabled ? "Test published" : "Test turned off (hidden from students)");
    } catch {
      setError("Failed to update visibility.");
    }
  };

  const handleToggleLocked = async (test) => {
    try {
      const updated = await lockTest(test.id, !test.locked);
      updateInState(updated, test.sessionId);
      setToast(updated.locked ? "Test locked" : "Test unlocked");
    } catch {
      setError("Failed to update lock status.");
    }
  };

  const updateInState = (updated, sessionId) => {
    if (sessionId != null) {
      setSessionTests((prev) => ({
        ...prev,
        [sessionId]: (prev[sessionId] || []).map((t) =>
          t.id === updated.id ? updated : t,
        ),
      }));
    } else {
      setTests((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    }
  };

  // scopeKey: null = class-level, otherwise a sessionId. Reorder + move share
  // this one code path, same convention as DocumentsTab.
  const getScopeTests = (scopeKey) =>
    scopeKey === null ? tests || [] : sessionTests[scopeKey] || [];

  const setScopeTests = (scopeKey, updater) => {
    if (scopeKey === null) {
      setTests(updater);
    } else {
      setSessionTests((prev) => ({ ...prev, [scopeKey]: updater(prev[scopeKey] || []) }));
    }
  };

  const handleDrop = async (item, targetScopeKey, targetItemId) => {
    if (!editContent || !item) return;
    const sourceScopeKey = item.sessionId ?? null;
    const crossScope = sourceScopeKey !== targetScopeKey;

    const prevSource = getScopeTests(sourceScopeKey);
    const prevTarget = crossScope ? getScopeTests(targetScopeKey) : prevSource;

    const targetExisting = prevTarget
      .filter((t) => t.id !== item.id)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const insertAt =
      targetItemId == null
        ? targetExisting.length
        : Math.max(0, targetExisting.findIndex((t) => t.id === targetItemId));

    const movedItem = { ...item, sessionId: targetScopeKey };
    const newOrderIds = [
      ...targetExisting.slice(0, insertAt).map((t) => t.id),
      item.id,
      ...targetExisting.slice(insertAt).map((t) => t.id),
    ];
    const newTargetList = newOrderIds.map((id, i) => {
      const found = id === item.id ? movedItem : targetExisting.find((t) => t.id === id);
      return { ...found, displayOrder: i };
    });

    if (crossScope) {
      setScopeTests(sourceScopeKey, (list) => list.filter((t) => t.id !== item.id));
    }
    setScopeTests(targetScopeKey, () => newTargetList);

    try {
      if (crossScope) {
        await moveTest(item.id, targetScopeKey);
      }
      if (targetScopeKey === null) {
        await reorderClassTests(courseId, newOrderIds);
      } else {
        await reorderSessionTests(courseId, targetScopeKey, newOrderIds);
      }
      if (crossScope) setToast("Test moved");
    } catch {
      setScopeTests(sourceScopeKey, () => prevSource);
      if (crossScope) setScopeTests(targetScopeKey, () => prevTarget);
    }
  };

  const makeDropHandler = (scopeKey) => (targetItemId, movingItem) =>
    handleDrop(movingItem || draggedTest, scopeKey, targetItemId ?? null);

  const handleAssess = (test) => {
    const base = window.location.pathname.includes("/course-hub/")
      ? "course-hub"
      : "course-management";
    navigate(`/${base}/${courseId}/tests/${test.id}/assess`);
  };

  // ── Filtering ────────────────────────────────────────────────────────────

  const allTests = React.useMemo(() => tests || [], [tests]);
  const filtered = React.useMemo(() => filterTests(allTests, filter, query), [allTests, filter, query]);
  const counts = countByStatus(allTests);
  const totalStudents = course?.studentCount ?? classSize ?? 0;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="tl-root">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Toolbar
        filter={filter}
        setFilter={setFilter}
        counts={counts}
        query={query}
        setQuery={setQuery}
        onAddTest={() => handleOpenCreate(null)}
        onFromBank={() => setBankModalOpen(true)}
        canEdit={editContent}
      />

      {/* Class-level Tests */}
      <section className="sec">
        <div className="sh">
          <div>
            <h2>Class-level Tests</h2>
            <p>
              {filtered.length} of {allTests.length} · visible to all enrolled students once published.
              {!canEdit && " Tests are managed by your training center administrator."}
              {contentLocked && " This class uses a standardized course: its tests cannot be changed."}
            </p>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="list">
            <div className="empty">
              <b>{allTests.length === 0 ? "No tests yet" : "No tests match these filters"}</b>
              {allTests.length === 0
                ? editContent
                  ? 'Click "New Test" to create your first written test.'
                  : "No tests have been created for this course yet."
                : "Try a different status or clear the search."}
            </div>
          </div>
        ) : (
          <div
            className={`list${classDropOver ? " over" : ""}`}
            onDragOver={(e) => { if (editContent && draggedTest) { e.preventDefault(); setClassDropOver(true); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setClassDropOver(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setClassDropOver(false);
              if (editContent && draggedTest) makeDropHandler(null)(null);
            }}
          >
            {filtered.map((test, idx) => (
              <TestCard
                key={test.id}
                test={test}
                canEdit={canEdit}
                contentLocked={contentLocked}
                progress={progressByTestId[test.id]}
                totalStudents={totalStudents}
                onEdit={() => handleOpenEdit(test, null)}
                onDelete={() => setDeleteTarget(test)}
                onToggleEnabled={() => handleToggleEnabled(test)}
                onToggleLocked={() => handleToggleLocked(test)}
                onAssess={() => handleAssess(test)}
                onAnswerKey={() => setKeyTest(test)}
                sessions={sessions}
                onMove={editContent ? (item, targetSessionId) => makeDropHandler(targetSessionId)(null, item) : null}
                draggable={editContent}
                onDragStart={setDraggedTest}
                onDragEnd={() => setDraggedTest(null)}
                dragging={draggedTest?.id === test.id}
                canDropHere={editContent && draggedTest && draggedTest.id !== test.id}
                onDropBefore={() => makeDropHandler(null)(test.id)}
                showReorder={editContent && filtered.length > 1}
                moveUpDisabled={idx === 0}
                moveDownDisabled={idx === filtered.length - 1}
                onMoveUp={() => makeDropHandler(null)(filtered[idx - 1]?.id ?? null, test)}
                onMoveDown={() => makeDropHandler(null)(filtered[idx + 2]?.id ?? null, test)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Session Tests */}
      {sessions && sessions.length > 0 && (
        <section className="sec">
          <div className="sh">
            <div>
              <h2>Session Tests</h2>
              <p>Short quizzes attached to a specific session. Drag to reorder, or onto another session to move.</p>
            </div>
          </div>
          <div className="list">
            {sessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                sessionTests={sessionTests[s.id]}
                canEdit={canEdit}
                contentLocked={contentLocked}
                progressByTestId={progressByTestId}
                totalStudents={totalStudents}
                onAddTest={() => handleOpenCreate(s.id)}
                onEdit={(test) => handleOpenEdit(test, s.id)}
                onDelete={(test) => setDeleteTarget(test)}
                onToggleEnabled={handleToggleEnabled}
                onToggleLocked={handleToggleLocked}
                onAssess={handleAssess}
                onAnswerKey={setKeyTest}
                sessions={sessions}
                onMove={(item, targetSessionId) => makeDropHandler(targetSessionId)(null, item)}
                draggedItem={draggedTest}
                onDragStartItem={setDraggedTest}
                onDragEndItem={() => setDraggedTest(null)}
                onDropTarget={() => makeDropHandler(s.id)(null)}
                onDropOnItem={makeDropHandler(s.id)}
              />
            ))}
          </div>
        </section>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast("")} message={toast} />

      <AnswerKeyDialog test={keyTest} onClose={() => setKeyTest(null)} />

      {/* Create / Edit modal */}
      <CreateTestModal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initial={editTarget}
      />

      <SaveToBankPromptDialog
        open={!!bankPromptTarget}
        sourceLabel="test"
        onClose={() => setBankPromptTarget(null)}
        onChoose={async (mode) => {
          const target = bankPromptTarget;
          setBankPromptTarget(null);
          if (mode === "KEEP_LOCAL" || !target) return;
          try {
            await pushTestToBank(target.id, mode);
            setToast("Saved to the test bank");
          } catch (e) {
            setError(e?.response?.data?.error || e?.response?.data?.message || "Could not update the test bank.");
          }
        }}
      />

      <AddFromBankModal
        open={bankModalOpen}
        onClose={() => setBankModalOpen(false)}
        courseId={courseId}
        type="tests"
        onAdded={(created) => {
          if (created?.id) {
            setTests((prev) => {
              const list = Array.isArray(prev) ? prev : [];
              if (list.some((t) => String(t.id) === String(created.id))) return list;
              return [...list, created];
            });
          }
        }}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle sx={{ fontFamily: "var(--font-display)" }}>Delete Test?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.title}</strong>? All student submissions
            will also be removed. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
